import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import GallerySection from './components/GallerySection';

// Hook para contador animado
function useCountUp(end: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, start]);

  return count;
}

interface StatCounterProps {
  end: number;
  suffix?: string;
  prefix?: string;
  label: string;
  icon: string;
  description: string;
  started: boolean;
  duration?: number;
}

function StatCounter({ end, suffix = '', prefix = '', label, icon, description, started, duration = 2000 }: StatCounterProps) {
  const count = useCountUp(end, duration, started);
  return (
    <div className="flex flex-col items-center text-center group">
      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 mb-5 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
        <i className={`${icon} text-[#D4AF37] text-2xl`}></i>
      </div>
      <div className="text-5xl md:text-6xl font-bold text-white mb-2 tabular-nums">
        <span className="text-[#D4AF37]">{prefix}</span>
        {count.toLocaleString()}
        <span className="text-[#D4AF37]">{suffix}</span>
      </div>
      <div className="text-white font-semibold text-lg mb-2">{label}</div>
      <div className="text-[#E2E8F0]/50 text-sm font-light max-w-[180px] leading-relaxed">{description}</div>
    </div>
  );
}

export default function HomePage() {
  const [formData, setFormData] = useState({
    nombre: '',
    correo: '',
    telefono: '',
    tipoProyecto: '',
    presupuesto: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [countersStarted, setCountersStarted] = useState(false);
  const countersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !countersStarted) {
          setCountersStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (countersRef.current) observer.observe(countersRef.current);
    return () => observer.disconnect();
  }, [countersStarted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d6q8p1dsbgrc47fim320', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          correo: '',
          telefono: '',
          tipoProyecto: '',
          presupuesto: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#0F172A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#0F172A]/95 backdrop-blur-sm border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center gap-3">
              <div className="flex flex-col justify-center leading-none">
                <span className="text-white text-4xl font-black tracking-widest">GCP</span>
                <span className="text-[#D4AF37] text-sm font-light tracking-[0.35em] uppercase mt-1">Constructora</span>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#servicios" className="text-[#E2E8F0] hover:text-[#D4AF37] transition-colors text-sm font-light cursor-pointer">Servicios</a>
              <a href="#portafolio" className="text-[#E2E8F0] hover:text-[#D4AF37] transition-colors text-sm font-light cursor-pointer">Portafolio</a>
              <a href="#galeria" className="text-[#E2E8F0] hover:text-[#D4AF37] transition-colors text-sm font-light cursor-pointer">Galería</a>
              <a href="#metodologia" className="text-[#E2E8F0] hover:text-[#D4AF37] transition-colors text-sm font-light cursor-pointer">Metodología</a>
              <a href="#cotizacion" className="text-[#E2E8F0] hover:text-[#D4AF37] transition-colors text-sm font-light cursor-pointer">Cotización</a>
              <a href="#contacto" className="bg-[#D4AF37] text-[#0F172A] px-6 py-2.5 rounded-md text-sm font-semibold hover:bg-[#C4A137] transition-all cursor-pointer whitespace-nowrap">Contacto</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Fondo animado con efecto de movimiento */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Imagen principal con zoom lento */}
          <div
            className="absolute inset-0 scale-110"
            style={{
              backgroundImage: `url('https://public.readdy.ai/ai/img_res/9ca6c926da4b10e4e4525c38168cb678.jpg')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              animation: 'heroZoom 20s ease-in-out infinite alternate',
            }}
          />
          {/* Overlay de partículas / polvo de obra */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=Abstract%20dust%20particles%20floating%20in%20air%20construction%20site%20atmosphere%2C%20fine%20concrete%20powder%20suspended%20in%20golden%20light%20beams%2C%20industrial%20haze%2C%20bokeh%20effect%2C%20dark%20background%20with%20warm%20amber%20light%20rays%2C%20ultra%20detailed%20macro%20photography&width=1920&height=1080&seq=hero-gcp-dust-v11&orientation=landscape')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              animation: 'heroDrift 15s ease-in-out infinite alternate',
            }}
          />
          {/* Overlay degradado oscuro */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0F172A]/95" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/40 via-transparent to-[#0F172A]/40" />
        </div>

        <style>{`
          @keyframes heroZoom {
            0%   { transform: scale(1.05) translateX(0px) translateY(0px); }
            33%  { transform: scale(1.12) translateX(-15px) translateY(-8px); }
            66%  { transform: scale(1.08) translateX(10px) translateY(-12px); }
            100% { transform: scale(1.15) translateX(-8px) translateY(5px); }
          }
          @keyframes heroDrift {
            0%   { transform: translateX(0px) translateY(0px) scale(1); opacity: 0.15; }
            50%  { transform: translateX(20px) translateY(-10px) scale(1.05); opacity: 0.25; }
            100% { transform: translateX(-15px) translateY(8px) scale(1.02); opacity: 0.18; }
          }
        `}</style>

        {/* Contenido Hero */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center w-full">
          {/* Badge superior */}
          <div className="inline-flex items-center gap-2 bg-white/5 border border-[#D4AF37]/30 rounded-full px-5 py-2 mb-8 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse inline-block"></span>
            <span className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase">+15 Años de Excelencia en Ingeniería</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Ingeniería de Alta Escala.<br />
            <span className="text-[#D4AF37]">Precisión Estética y Deportiva.</span>
          </h1>
          <h2 className="text-xl md:text-2xl text-[#E2E8F0] font-light mb-12 max-w-4xl mx-auto leading-relaxed">
            GCP Constructora: Más de 15 años orquestando proyectos integrales. Desde cimentaciones profundas y retail de lujo, hasta la rehabilitación deportiva bajo normativas estrictas.
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#contacto"
              className="inline-block bg-[#D4AF37] text-[#0F172A] px-10 py-4 rounded-lg text-lg font-bold hover:bg-[#C4A137] transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl shadow-[#D4AF37]/30"
            >
              Solicitar Diagnóstico de Proyecto
            </a>
            <a
              href="#portafolio"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 rounded-lg text-base font-light hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              <i className="ri-play-circle-line text-xl"></i>
              Ver Portafolio
            </a>
          </div>
        </div>

        {/* Indicador de scroll */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-[#D4AF37] text-3xl"></i>
        </div>

        {/* Indicador de video en vivo */}
        <div className="absolute bottom-8 right-8 flex items-center gap-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block"></span>
          <span className="text-white/70 text-xs font-light tracking-wider">OBRA EN VIVO</span>
        </div>
      </section>

      {/* Franja de Autoridad */}
      <section className="bg-[#18181B] border-y border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <div className="text-[#D4AF37] text-3xl font-bold mb-2">+15</div>
              <div className="text-[#E2E8F0] text-sm font-light">Años de Experiencia Conjunta</div>
            </div>
            <div className="flex flex-col items-center border-l border-white/10">
              <div className="text-[#D4AF37] text-lg font-semibold mb-2">Cimentaciones</div>
              <div className="text-[#E2E8F0] text-sm font-light">Top Down</div>
            </div>
            <div className="flex flex-col items-center border-l border-white/10">
              <div className="text-[#D4AF37] text-lg font-semibold mb-2">Retail</div>
              <div className="text-[#E2E8F0] text-sm font-light">de Lujo</div>
            </div>
            <div className="flex flex-col items-center border-l border-white/10">
              <div className="text-[#D4AF37] text-lg font-semibold mb-2">Infraestructura</div>
              <div className="text-[#E2E8F0] text-sm font-light">Deportiva y Urbana</div>
            </div>
          </div>
        </div>
      </section>

      {/* Sección Contadores Animados */}
      <section className="py-24 bg-[#18181B] relative overflow-hidden">
        {/* Fondo decorativo */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.3em] mb-3">Trayectoria Comprobada</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Cifras que Hablan por Sí Solas</h2>
            <p className="text-[#E2E8F0] text-lg font-light max-w-2xl mx-auto">
              Décadas de ingeniería de alta escala respaldadas por resultados concretos
            </p>
          </div>

          <div ref={countersRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            <StatCounter
              end={15}
              suffix="+"
              label="Años de Experiencia"
              icon="ri-award-line"
              description="Trayectoria ininterrumpida en ingeniería de alta complejidad"
              started={countersStarted}
              duration={1800}
            />
            <StatCounter
              end={50}
              prefix="+"
              label="Proyectos Completados"
              icon="ri-building-2-line"
              description="Desde cimentaciones profundas hasta retail de lujo"
              started={countersStarted}
              duration={2000}
            />
            <StatCounter
              end={200}
              prefix="+"
              suffix="K"
              label="m² Construidos"
              icon="ri-ruler-2-line"
              description="Metros cuadrados ejecutados con precisión paramétrica"
              started={countersStarted}
              duration={2200}
            />
            <StatCounter
              end={100}
              suffix="%"
              label="Clientes Satisfechos"
              icon="ri-thumb-up-line"
              description="Cero proyectos con retraso o incumplimiento de entrega"
              started={countersStarted}
              duration={1600}
            />
          </div>

          {/* Línea divisoria decorativa */}
          <div className="mt-20 flex items-center gap-6">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[#D4AF37]/30"></div>
            <div className="flex items-center gap-2 text-[#D4AF37]/60 text-xs font-semibold uppercase tracking-widest whitespace-nowrap">
              <i className="ri-shield-check-line"></i>
              Calidad Certificada · Entrega Garantizada
            </div>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[#D4AF37]/30"></div>
          </div>
        </div>
      </section>

      {/* Nuestras Divisiones */}
      <section id="servicios" className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Nuestras Divisiones</h2>
            <p className="text-[#E2E8F0] text-lg font-light max-w-3xl mx-auto">
              Tres pilares de excelencia que transforman visiones en realidades construidas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Tarjeta 1: Obra Civil */}
            <div className="group relative bg-[#18181B] rounded-xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/50 transition-all duration-500 hover:transform hover:scale-105">
              <div className="relative h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Cinematic%208k%20close-up%20photography%20of%20Milan%20Wall%20deep%20foundation%20construction%2C%20massive%20heavy%20machinery%20excavating%2C%20dramatic%20industrial%20lighting%2C%20concrete%20structures%2C%20professional%20engineering%20site%2C%20dark%20moody%20atmosphere%20with%20golden%20accents%2C%20ultra%20detailed%20architectural%20photography%2C%20construction%20excellence&width=800&height=800&seq=service-civil-001&orientation=squarish" 
                  alt="Obra Civil y Retail" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#18181B] via-transparent to-transparent"></div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Obra Civil y Retail</h3>
                <p className="text-[#E2E8F0] font-light leading-relaxed mb-6">
                  Ejecución de cimentaciones (Muro Milán), estructuras metálicas, edificación residencial premium y adecuaciones para marcas de lujo.
                </p>
                <div className="flex items-center text-[#D4AF37] font-semibold cursor-pointer group-hover:translate-x-2 transition-transform">
                  <span className="text-sm whitespace-nowrap">Conocer más</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>
            </div>

            {/* Tarjeta 2: División Deportiva */}
            <div className="group relative bg-[#18181B] rounded-xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/50 transition-all duration-500 hover:transform hover:scale-105">
              <div className="relative h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Macro%208k%20photography%20of%20pristine%20sports%20court%20surface%20with%20perfect%20acrylic%20paint%20lines%20in%20blue%20and%20green%2C%20professional%20concrete%20texture%2C%20new%20chain%20link%20fence%20blurred%20in%20background%2C%20dramatic%20lighting%2C%20ultra%20detailed%20surface%20texture%2C%20professional%20sports%20facility%2C%20premium%20quality%20finish&width=800&height=800&seq=service-sports-001&orientation=squarish" 
                  alt="División Deportiva" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#18181B] via-transparent to-transparent"></div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-4">División Deportiva</h3>
                <p className="text-[#E2E8F0] font-light leading-relaxed mb-6">
                  Rehabilitación funcional de espacios de alto rendimiento. Recubrimientos acrílicos, normatividad NMX, seguridad perimetral e iluminación.
                </p>
                <div className="flex items-center text-[#D4AF37] font-semibold cursor-pointer group-hover:translate-x-2 transition-transform">
                  <span className="text-sm whitespace-nowrap">Conocer más</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>
            </div>

            {/* Tarjeta 3: Gerencia Ejecutiva */}
            <div className="group relative bg-[#18181B] rounded-xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/50 transition-all duration-500 hover:transform hover:scale-105">
              <div className="relative h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%208k%20photography%20of%20iPad%20displaying%20parametric%20budgets%20and%20architectural%20blueprints%20on%20polished%20concrete%20desk%2C%20modern%20office%20setting%2C%20dramatic%20lighting%2C%20business%20documents%2C%20construction%20plans%2C%20premium%20workspace%2C%20dark%20moody%20atmosphere%20with%20golden%20accents%2C%20ultra%20detailed&width=800&height=800&seq=service-management-001&orientation=squarish" 
                  alt="Gerencia Ejecutiva de Obra" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#18181B] via-transparent to-transparent"></div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Gerencia Ejecutiva de Obra</h3>
                <p className="text-[#E2E8F0] font-light leading-relaxed mb-6">
                  Control paramétrico de presupuestos, manejo estricto de bitácora, generadores y coordinación. Llave en Mano.
                </p>
                <div className="flex items-center text-[#D4AF37] font-semibold cursor-pointer group-hover:translate-x-2 transition-transform">
                  <span className="text-sm whitespace-nowrap">Conocer más</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portafolio Destacado */}
      <section id="portafolio" className="py-24 bg-[#18181B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Portafolio Destacado</h2>
            <p className="text-[#E2E8F0] text-lg font-light max-w-3xl mx-auto">
              Proyectos que definen estándares de excelencia en la industria
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Proyecto 1 */}
            <div className="group relative bg-[#0F172A] rounded-lg overflow-hidden border border-white/5 hover:border-[#D4AF37]/50 transition-all duration-300">
              <div className="relative h-64 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Luxury%20retail%20interior%20construction%20Cartier%20boutique%20high-end%20finishes%20premium%20materials%20elegant%20architecture%20sophisticated%20lighting%208k%20photography%20dark%20moody%20atmosphere%20with%20golden%20accents&width=800&height=600&seq=project-cartier-001&orientation=landscape" 
                  alt="Cartier Masaryk" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent"></div>
              </div>
              <div className="p-6">
                <div className="text-[#D4AF37] text-xs font-semibold mb-2 uppercase tracking-wider">Retail de Lujo</div>
                <h3 className="text-xl font-bold text-white mb-3">Cartier Masaryk</h3>
                <p className="text-[#E2E8F0] text-sm font-light leading-relaxed">
                  Residencia de obra y acabados de lujo (600 m²)
                </p>
              </div>
            </div>

            {/* Proyecto 2 */}
            <div className="group relative bg-[#0F172A] rounded-lg overflow-hidden border border-white/5 hover:border-[#D4AF37]/50 transition-all duration-300">
              <div className="relative h-64 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Massive%20deep%20foundation%20construction%20Milan%20Wall%20excavation%2054000%20cubic%20meters%20concrete%20structures%20heavy%20machinery%20industrial%20scale%20engineering%20project%208k%20photography%20dramatic%20lighting%20construction%20excellence&width=800&height=600&seq=project-oceania-001&orientation=landscape" 
                  alt="Encuentro Oceanía" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent"></div>
              </div>
              <div className="p-6">
                <div className="text-[#D4AF37] text-xs font-semibold mb-2 uppercase tracking-wider">Cimentación Profunda</div>
                <h3 className="text-xl font-bold text-white mb-3">Encuentro Oceanía</h3>
                <p className="text-[#E2E8F0] text-sm font-light leading-relaxed">
                  Cimentación profunda, 54,000 m³ y Muro Milán
                </p>
              </div>
            </div>

            {/* Proyecto 3 */}
            <div className="group relative bg-[#0F172A] rounded-lg overflow-hidden border border-white/5 hover:border-[#D4AF37]/50 transition-all duration-300">
              <div className="relative h-64 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Highway%20infrastructure%20project%20Viaducto%20Bicentenario%2054%20kilometers%20urban%20development%20construction%20generators%20engineering%20planning%20aerial%20view%208k%20photography%20dramatic%20lighting%20modern%20infrastructure&width=800&height=600&seq=project-viaducto-001&orientation=landscape" 
                  alt="Viaducto Bicentenario" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent"></div>
              </div>
              <div className="p-6">
                <div className="text-[#D4AF37] text-xs font-semibold mb-2 uppercase tracking-wider">Infraestructura Urbana</div>
                <h3 className="text-xl font-bold text-white mb-3">Viaducto Bicentenario</h3>
                <p className="text-[#E2E8F0] text-sm font-light leading-relaxed">
                  Cuantificación y generadores en 54 km
                </p>
              </div>
            </div>

            {/* Proyecto 4 */}
            <div className="group relative bg-[#0F172A] rounded-lg overflow-hidden border border-white/5 hover:border-[#D4AF37]/50 transition-all duration-300">
              <div className="relative h-64 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20sports%20court%20rehabilitation%20acrylic%20coating%20perfect%20lines%20chain%20link%20fence%20LED%20lighting%20athletic%20facility%20renovation%208k%20photography%20dramatic%20nighttime%20lighting%20sports%20infrastructure%20excellence&width=800&height=600&seq=project-sports-001&orientation=landscape" 
                  alt="Infraestructura Deportiva" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent"></div>
              </div>
              <div className="p-6">
                <div className="text-[#D4AF37] text-xs font-semibold mb-2 uppercase tracking-wider">División Deportiva</div>
                <h3 className="text-xl font-bold text-white mb-3">Infraestructura Deportiva</h3>
                <p className="text-[#E2E8F0] text-sm font-light leading-relaxed">
                  Rehabilitación de canchas, recubrimientos y mallas
                </p>
              </div>
            </div>

            {/* Proyecto 5 */}
            <div className="group relative bg-[#0F172A] rounded-lg overflow-hidden border border-white/5 hover:border-[#D4AF37]/50 transition-all duration-300 md:col-span-2 lg:col-span-1">
              <div className="relative h-64 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Premium%20residential%20apartments%20construction%20luxury%20building%202293%20square%20meters%20modern%20architecture%20high-end%20finishes%20elegant%20facade%208k%20photography%20dramatic%20lighting%20residential%20excellence%20sophisticated%20design&width=800&height=600&seq=project-calderon-001&orientation=landscape" 
                  alt="Calderón de la Barca 93" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent"></div>
              </div>
              <div className="p-6">
                <div className="text-[#D4AF37] text-xs font-semibold mb-2 uppercase tracking-wider">Residencial Premium</div>
                <h3 className="text-xl font-bold text-white mb-3">Calderón de la Barca 93</h3>
                <p className="text-[#E2E8F0] text-sm font-light leading-relaxed">
                  Departamentos premium (2,293 m²)
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Galería de Proyectos */}
      <GallerySection />

      {/* Metodología GCP */}
      <section id="metodologia" className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Metodología GCP</h2>
            <p className="text-[#E2E8F0] text-lg font-light max-w-3xl mx-auto">
              Por qué no fallamos: Un proceso probado que garantiza resultados excepcionales
            </p>
          </div>

          <div className="relative">
            {/* Línea de conexión */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-[#D4AF37]/20 via-[#D4AF37] to-[#D4AF37]/20 transform -translate-y-1/2"></div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10">
              {/* Paso 1 */}
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center mb-6 shadow-2xl shadow-[#D4AF37]/50">
                  <span className="text-[#0F172A] text-2xl font-bold">1</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Diagnóstico Técnico</h3>
                <p className="text-[#E2E8F0] text-sm font-light leading-relaxed">
                  Análisis exhaustivo de viabilidad, normativas y requerimientos específicos del proyecto
                </p>
              </div>

              {/* Paso 2 */}
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center mb-6 shadow-2xl shadow-[#D4AF37]/50">
                  <span className="text-[#0F172A] text-2xl font-bold">2</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Ingeniería Paramétrica</h3>
                <p className="text-[#E2E8F0] text-sm font-light leading-relaxed">
                  Desarrollo de presupuestos detallados y planificación estratégica con control total
                </p>
              </div>

              {/* Paso 3 */}
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center mb-6 shadow-2xl shadow-[#D4AF37]/50">
                  <span className="text-[#0F172A] text-2xl font-bold">3</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Ejecución Orquestada</h3>
                <p className="text-[#E2E8F0] text-sm font-light leading-relaxed">
                  Coordinación precisa de recursos, tiempos y equipos con supervisión continua
                </p>
              </div>

              {/* Paso 4 */}
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center mb-6 shadow-2xl shadow-[#D4AF37]/50">
                  <span className="text-[#0F172A] text-2xl font-bold">4</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Entrega Patrimonial</h3>
                <p className="text-[#E2E8F0] text-sm font-light leading-relaxed">
                  Finalización impecable con documentación completa y garantías de calidad total
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proceso de Cotización */}
      <section id="cotizacion" className="py-24 bg-[#18181B] relative overflow-hidden">
        {/* Fondo decorativo */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-0 w-80 h-80 bg-[#D4AF37]/4 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-0 w-80 h-80 bg-[#D4AF37]/4 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Header */}
          <div className="text-center mb-20">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.3em] mb-3">Simple y Transparente</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Proceso de Cotización</h2>
            <p className="text-[#E2E8F0] text-lg font-light max-w-3xl mx-auto">
              De su primera llamada al inicio de obra en 5 pasos claros. Sin sorpresas, sin letra chica.
            </p>
          </div>

          {/* Timeline de pasos */}
          <div className="relative">
            {/* Línea vertical central (mobile) */}
            <div className="md:hidden absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#D4AF37]/80 via-[#D4AF37]/40 to-transparent"></div>

            {/* Línea horizontal (desktop) */}
            <div className="hidden md:block absolute top-[52px] left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent"></div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">

              {/* Paso 1 */}
              <div className="relative flex md:flex-col items-start md:items-center gap-6 md:gap-0 md:text-center group">
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D4AF37] shadow-2xl shadow-[#D4AF37]/40 z-10 relative group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-phone-line text-[#0F172A] text-2xl"></i>
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full bg-[#0F172A] border-2 border-[#D4AF37] z-20">
                    <span className="text-[#D4AF37] text-xs font-bold">1</span>
                  </div>
                </div>
                <div className="md:mt-6 flex-1 md:flex-none">
                  <h3 className="text-white font-bold text-base mb-2 group-hover:text-[#D4AF37] transition-colors">Primer Contacto</h3>
                  <p className="text-[#E2E8F0]/60 text-sm font-light leading-relaxed">
                    Nos llama, escribe o llena el formulario. Respondemos en menos de 24 horas hábiles.
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full px-3 py-1">
                    <i className="ri-time-line text-[#D4AF37] text-xs"></i>
                    <span className="text-[#D4AF37] text-xs font-semibold">24 hrs</span>
                  </div>
                </div>
              </div>

              {/* Paso 2 */}
              <div className="relative flex md:flex-col items-start md:items-center gap-6 md:gap-0 md:text-center group">
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1E2535] border-2 border-[#D4AF37]/50 shadow-xl z-10 relative group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-all duration-300">
                    <i className="ri-file-list-3-line text-[#D4AF37] group-hover:text-[#0F172A] text-2xl transition-colors duration-300"></i>
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full bg-[#0F172A] border-2 border-[#D4AF37]/50 z-20">
                    <span className="text-[#D4AF37] text-xs font-bold">2</span>
                  </div>
                </div>
                <div className="md:mt-6 flex-1 md:flex-none">
                  <h3 className="text-white font-bold text-base mb-2 group-hover:text-[#D4AF37] transition-colors">Diagnóstico Técnico</h3>
                  <p className="text-[#E2E8F0]/60 text-sm font-light leading-relaxed">
                    Reunión presencial o virtual para entender alcances, normativas y requerimientos específicos.
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full px-3 py-1">
                    <i className="ri-time-line text-[#D4AF37] text-xs"></i>
                    <span className="text-[#D4AF37] text-xs font-semibold">2–3 días</span>
                  </div>
                </div>
              </div>

              {/* Paso 3 */}
              <div className="relative flex md:flex-col items-start md:items-center gap-6 md:gap-0 md:text-center group">
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1E2535] border-2 border-[#D4AF37]/50 shadow-xl z-10 relative group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-all duration-300">
                    <i className="ri-calculator-line text-[#D4AF37] group-hover:text-[#0F172A] text-2xl transition-colors duration-300"></i>
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full bg-[#0F172A] border-2 border-[#D4AF37]/50 z-20">
                    <span className="text-[#D4AF37] text-xs font-bold">3</span>
                  </div>
                </div>
                <div className="md:mt-6 flex-1 md:flex-none">
                  <h3 className="text-white font-bold text-base mb-2 group-hover:text-[#D4AF37] transition-colors">Propuesta Paramétrica</h3>
                  <p className="text-[#E2E8F0]/60 text-sm font-light leading-relaxed">
                    Entregamos presupuesto detallado con generadores, partidas y cronograma de ejecución.
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full px-3 py-1">
                    <i className="ri-time-line text-[#D4AF37] text-xs"></i>
                    <span className="text-[#D4AF37] text-xs font-semibold">5–7 días</span>
                  </div>
                </div>
              </div>

              {/* Paso 4 */}
              <div className="relative flex md:flex-col items-start md:items-center gap-6 md:gap-0 md:text-center group">
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1E2535] border-2 border-[#D4AF37]/50 shadow-xl z-10 relative group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-all duration-300">
                    <i className="ri-shake-hands-line text-[#D4AF37] group-hover:text-[#0F172A] text-2xl transition-colors duration-300"></i>
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full bg-[#0F172A] border-2 border-[#D4AF37]/50 z-20">
                    <span className="text-[#D4AF37] text-xs font-bold">4</span>
                  </div>
                </div>
                <div className="md:mt-6 flex-1 md:flex-none">
                  <h3 className="text-white font-bold text-base mb-2 group-hover:text-[#D4AF37] transition-colors">Acuerdo y Contrato</h3>
                  <p className="text-[#E2E8F0]/60 text-sm font-light leading-relaxed">
                    Revisión conjunta, ajustes finales y firma de contrato con condiciones claras y garantías.
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full px-3 py-1">
                    <i className="ri-time-line text-[#D4AF37] text-xs"></i>
                    <span className="text-[#D4AF37] text-xs font-semibold">1–2 días</span>
                  </div>
                </div>
              </div>

              {/* Paso 5 */}
              <div className="relative flex md:flex-col items-start md:items-center gap-6 md:gap-0 md:text-center group">
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1E2535] border-2 border-[#D4AF37]/50 shadow-xl z-10 relative group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-all duration-300">
                    <i className="ri-hammer-line text-[#D4AF37] group-hover:text-[#0F172A] text-2xl transition-colors duration-300"></i>
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full bg-[#0F172A] border-2 border-[#D4AF37]/50 z-20">
                    <span className="text-[#D4AF37] text-xs font-bold">5</span>
                  </div>
                </div>
                <div className="md:mt-6 flex-1 md:flex-none">
                  <h3 className="text-white font-bold text-base mb-2 group-hover:text-[#D4AF37] transition-colors">Inicio de Obra</h3>
                  <p className="text-[#E2E8F0]/60 text-sm font-light leading-relaxed">
                    Movilización de equipo, apertura de bitácora y arranque formal con supervisión continua.
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full px-3 py-1">
                    <i className="ri-rocket-line text-[#D4AF37] text-xs"></i>
                    <span className="text-[#D4AF37] text-xs font-semibold">¡A construir!</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Banner inferior CTA */}
          <div className="mt-20 relative rounded-2xl overflow-hidden border border-[#D4AF37]/20">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Wide%20angle%20cinematic%20photography%20of%20luxury%20construction%20site%20at%20dusk%2C%20tower%20cranes%20silhouetted%20against%20dramatic%20orange%20and%20deep%20blue%20sky%2C%20warm%20golden%20construction%20lights%2C%20concrete%20and%20steel%20structures%2C%20professional%20engineering%20megaproject%2C%20dark%20moody%20atmosphere%20with%20rich%20amber%20highlights%2C%20ultra%20photorealistic%208k&width=1200&height=300&seq=cta-cotizacion-banner-v1&orientation=landscape"
                alt="Inicio de obra GCP"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/80 to-[#0F172A]/60"></div>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 px-10 py-10">
              <div>
                <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-2">Sin costo ni compromiso</p>
                <h3 className="text-white text-2xl md:text-3xl font-bold mb-1">¿Listo para el Paso 1?</h3>
                <p className="text-[#E2E8F0]/70 text-sm font-light">El diagnóstico técnico inicial es completamente gratuito.</p>
              </div>
              <a
                href="#contacto"
                className="flex-shrink-0 bg-[#D4AF37] text-[#0F172A] px-8 py-4 rounded-lg text-base font-bold hover:bg-[#C4A137] transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl shadow-[#D4AF37]/30"
              >
                Solicitar Diagnóstico Gratuito
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonios y Clientes Destacados */}
      <section id="clientes" className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Clientes / Logos */}
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.3em] mb-3">Confianza Comprobada</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Clientes que Respaldan Nuestra Excelencia</h2>
            <p className="text-[#E2E8F0] text-lg font-light max-w-3xl mx-auto">
              Marcas y desarrolladores de primer nivel que han confiado en GCP Constructora para sus proyectos más exigentes
            </p>
          </div>

          {/* Logos de clientes */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
            {[
              { name: "Cartier", sector: "Retail de Lujo" },
              { name: "Encuentro Oceanía", sector: "Desarrollo Comercial" },
              { name: "Viaducto Bicentenario", sector: "Infraestructura Urbana" },
              { name: "CDMX Deportes", sector: "Infraestructura Deportiva" },
            ].map((client) => (
              <div
                key={client.name}
                className="bg-[#18181B] border border-white/5 hover:border-[#D4AF37]/40 rounded-xl p-6 flex flex-col items-center justify-center text-center transition-all duration-300 hover:bg-[#1E2535] group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/10 rounded-full mb-4 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <i className="ri-building-2-line text-[#D4AF37] text-xl"></i>
                </div>
                <span className="text-white font-semibold text-sm mb-1">{client.name}</span>
                <span className="text-[#E2E8F0]/50 text-xs font-light">{client.sector}</span>
              </div>
            ))}
          </div>

          {/* Testimonios */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonio 1 */}
            <div className="bg-[#18181B] border border-white/5 hover:border-[#D4AF37]/30 rounded-xl p-8 transition-all duration-300 flex flex-col">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#D4AF37] text-sm mr-0.5"></i>
                ))}
              </div>
              <blockquote className="text-[#E2E8F0] font-light leading-relaxed text-sm mb-6 flex-1">
                "GCP Constructora ejecutó la adecuación de nuestra boutique Cartier en Masaryk con un nivel de detalle y acabados que superó nuestras expectativas. Su capacidad de coordinación y control de obra es verdaderamente excepcional."
              </blockquote>
              <div className="flex items-center gap-4 border-t border-white/10 pt-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#D4AF37]/15 flex-shrink-0">
                  <i className="ri-user-3-line text-[#D4AF37] text-lg"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Alejandro Ruiz</p>
                  <p className="text-[#E2E8F0]/50 text-xs font-light">Director de Proyectos — Cartier México</p>
                </div>
              </div>
            </div>

            {/* Testimonio 2 */}
            <div className="bg-[#18181B] border border-white/5 hover:border-[#D4AF37]/30 rounded-xl p-8 transition-all duration-300 flex flex-col">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#D4AF37] text-sm mr-0.5"></i>
                ))}
              </div>
              <blockquote className="text-[#E2E8F0] font-light leading-relaxed text-sm mb-6 flex-1">
                "La cimentación profunda del Encuentro Oceanía fue un reto técnico enorme. GCP demostró dominio total del proceso de Muro Milán, cumpliendo tiempos y presupuesto con una precisión que no habíamos visto antes en proyectos de esta escala."
              </blockquote>
              <div className="flex items-center gap-4 border-t border-white/10 pt-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#D4AF37]/15 flex-shrink-0">
                  <i className="ri-user-3-line text-[#D4AF37] text-lg"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Ing. Carlos Mendoza</p>
                  <p className="text-[#E2E8F0]/50 text-xs font-light">Gerente de Desarrollo — Grupo Inmobiliario Oceanía</p>
                </div>
              </div>
            </div>

            {/* Testimonio 3 */}
            <div className="bg-[#18181B] border border-white/5 hover:border-[#D4AF37]/30 rounded-xl p-8 transition-all duration-300 flex flex-col">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#D4AF37] text-sm mr-0.5"></i>
                ))}
              </div>
              <blockquote className="text-[#E2E8F0] font-light leading-relaxed text-sm mb-6 flex-1">
                "Contratamos a GCP para la rehabilitación de nuestras instalaciones deportivas y el resultado fue impecable. Recubrimientos acrílicos perfectos, iluminación LED de alto rendimiento y toda la normatividad NMX cubierta. Trabajo de primer nivel."
              </blockquote>
              <div className="flex items-center gap-4 border-t border-white/10 pt-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#D4AF37]/15 flex-shrink-0">
                  <i className="ri-user-3-line text-[#D4AF37] text-lg"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Lic. Patricia Vega</p>
                  <p className="text-[#E2E8F0]/50 text-xs font-light">Coordinadora — Secretaría de Deportes CDMX</p>
                </div>
              </div>
            </div>
          </div>

          {/* Cifras de impacto */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "+50", label: "Proyectos Completados", icon: "ri-building-line" },
              { value: "100%", label: "Clientes Satisfechos", icon: "ri-thumb-up-line" },
              { value: "+200K", label: "m² Construidos", icon: "ri-ruler-2-line" },
              { value: "0", label: "Proyectos con Retraso", icon: "ri-time-line" },
            ].map((stat) => (
              <div key={stat.label} className="bg-[#18181B] border border-white/5 rounded-xl p-6 text-center hover:border-[#D4AF37]/30 transition-all duration-300">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-[#D4AF37]/10 rounded-full">
                  <i className={`${stat.icon} text-[#D4AF37] text-lg`}></i>
                </div>
                <div className="text-[#D4AF37] text-3xl font-bold mb-1">{stat.value}</div>
                <div className="text-[#E2E8F0] text-xs font-light">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulario de Captación */}
      <section id="contacto" className="py-24 bg-[#18181B]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              ¿Listo para materializar su proyecto con el respaldo de expertos?
            </h2>
            <p className="text-[#E2E8F0] text-lg font-light">
              Hablemos de viabilidad, tiempos y presupuestos paramétricos.
            </p>
          </div>

          <form 
            id="contactForm"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-[#0F172A] rounded-xl p-8 md:p-12 border border-white/10 shadow-2xl"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="nombre" className="block text-[#E2E8F0] text-sm font-light mb-2">
                  Nombre Completo <span className="text-[#D4AF37]">*</span>
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#18181B] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                  placeholder="Juan Pérez"
                />
              </div>

              <div>
                <label htmlFor="correo" className="block text-[#E2E8F0] text-sm font-light mb-2">
                  Correo Empresarial <span className="text-[#D4AF37]">*</span>
                </label>
                <input
                  type="email"
                  id="correo"
                  name="email"
                  value={formData.correo}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#18181B] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                  placeholder="juan@empresa.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="telefono" className="block text-[#E2E8F0] text-sm font-light mb-2">
                  Teléfono <span className="text-[#D4AF37]">*</span>
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#18181B] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                  placeholder="55 1234 5678"
                />
              </div>

              <div>
                <label htmlFor="tipoProyecto" className="block text-[#E2E8F0] text-sm font-light mb-2">
                  Tipo de Proyecto <span className="text-[#D4AF37]">*</span>
                </label>
                <select
                  id="tipoProyecto"
                  name="tipoProyecto"
                  value={formData.tipoProyecto}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#18181B] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors cursor-pointer"
                >
                  <option value="">Seleccione una opción</option>
                  <option value="Obra Civil">Obra Civil</option>
                  <option value="Retail">Retail</option>
                  <option value="División Deportiva">División Deportiva</option>
                  <option value="Gerencia de Obra">Gerencia de Obra</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
            </div>

            <div className="mb-8">
              <label htmlFor="presupuesto" className="block text-[#E2E8F0] text-sm font-light mb-2">
                Presupuesto Estimado <span className="text-[#E2E8F0]/50">(Opcional)</span>
              </label>
              <input
                type="text"
                id="presupuesto"
                name="presupuesto"
                value={formData.presupuesto}
                onChange={handleChange}
                className="w-full bg-[#18181B] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="Ej: $5,000,000 MXN"
              />
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/50 rounded-lg">
                <p className="text-green-400 text-sm text-center">
                  ¡Gracias por contactarnos! Nos pondremos en contacto pronto.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
                <p className="text-red-400 text-sm text-center">
                  Hubo un error al enviar el formulario. Por favor, intente nuevamente.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D4AF37] text-[#0F172A] px-8 py-4 rounded-lg text-lg font-bold hover:bg-[#C4A137] transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none cursor-pointer whitespace-nowrap shadow-2xl"
            >
              {isSubmitting ? 'Enviando...' : 'Agendar Asesoría Técnica'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex flex-col justify-center leading-none mb-4">
                <span className="text-white text-3xl font-black tracking-widest">GCP</span>
                <span className="text-[#D4AF37] text-sm font-light tracking-[0.35em] uppercase mt-1">Constructora</span>
              </div>
              <p className="text-[#E2E8F0] text-sm font-light leading-relaxed">
                Más de 15 años orquestando proyectos integrales de ingeniería de alta escala.
              </p>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Servicios</h4>
              <ul className="space-y-2">
                <li><a href="#servicios" className="text-[#E2E8F0] text-sm font-light hover:text-[#D4AF37] transition-colors cursor-pointer">Obra Civil y Retail</a></li>
                <li><a href="#servicios" className="text-[#E2E8F0] text-sm font-light hover:text-[#D4AF37] transition-colors cursor-pointer">División Deportiva</a></li>
                <li><a href="#servicios" className="text-[#E2E8F0] text-sm font-light hover:text-[#D4AF37] transition-colors cursor-pointer">Gerencia Ejecutiva</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Contacto</h4>
              <ul className="space-y-2">
                <li className="flex items-center text-[#E2E8F0] text-sm font-light">
                  <i className="ri-mail-line text-[#D4AF37] mr-2"></i>
                  contacto@somosgcp.com
                </li>
                <li className="flex items-center text-[#E2E8F0] text-sm font-light">
                  <i className="ri-phone-line text-[#D4AF37] mr-2"></i>
                  55 6014 3063
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-[#E2E8F0] text-sm font-light">
              © 2024 GCP Constructora. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Botón flotante de WhatsApp */}
      <a
        href="https://wa.me/5215560143063?text=Hola%2C%20me%20interesa%20conocer%20m%C3%A1s%20sobre%20los%20servicios%20de%20GCP%20Constructora."
        target="_blank"
        rel="nofollow noopener noreferrer"
        aria-label="Contactar por WhatsApp"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white px-4 py-3 rounded-full shadow-2xl shadow-[#25D366]/40 transition-all duration-300 hover:scale-105 cursor-pointer group"
      >
        <div className="w-7 h-7 flex items-center justify-center">
          <i className="ri-whatsapp-line text-2xl leading-none"></i>
        </div>
        <span className="text-sm font-semibold whitespace-nowrap max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-500 ease-in-out">
          Escríbenos
        </span>
      </a>

    </div>
  );
}